import { zodResolver } from '@hookform/resolvers/zod'
import { Button, Flex, Heading, toast } from '@workwhile/ui'
import React from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import {
  companyTypeToFields,
  fieldsToCompanyType,
} from 'api/companies/companyType'
import { ModalWithSelect } from 'components/ModalSelect'
import { useCompanyDetailContextValue } from 'pages/Company/CompanyDetail/context/CompanyDetailProvider'
import { useCompanyDetailWorkflowsContextValue } from 'pages/Company/CompanyDetail/context/CompanyDetailWorkflowProvider'
import { GeneralDetailsForm } from 'pages/Company/CompanyDetail/workflows/EditGeneralDetailsFlow/components/GeneralDetailsForm'
import { useEditGeneralDetailsFlowContextValue } from 'pages/Company/CompanyDetail/workflows/EditGeneralDetailsFlow/context/EditGeneralDetailsFlowProvider'
import {
  EditGeneralDetailsFlowSchema,
  editGeneralDetailsFlowSchema,
} from 'pages/Company/CompanyDetail/workflows/EditGeneralDetailsFlow/schemas/editGeneralDetailsFlowSchema'

export const EditGeneralDetailsFlowMain = () => {
  const {
    editGeneralDetailsFlow: { isOpen, close },
  } = useCompanyDetailWorkflowsContextValue()

  const {
    state: {
      formData,
      mutationState: { isPending },
    },
    actions: { submitFormToServer, setFormData },
  } = useEditGeneralDetailsFlowContextValue()

  const {
    state: {
      type,
      requiresEverify,
      terminalTierThresholdHours,
      terminalTierThresholdDirection,
      pastShiftCreationAllowed,
    },
  } = useCompanyDetailContextValue()

  const companyTypeValue = fieldsToCompanyType(type, requiresEverify)

  // Defaults to true, matching the backend's default.
  const pastShiftCreationAllowedValue = pastShiftCreationAllowed ?? true

  // The company's real, persisted settings; the form resets to these on close.
  const realFormValues: EditGeneralDetailsFlowSchema = {
    type: companyTypeValue,
    terminalTierThresholdHours: terminalTierThresholdHours,
    terminalTierThresholdDirection: terminalTierThresholdDirection,
    pastShiftCreationAllowed: pastShiftCreationAllowedValue,
    currentValues: {
      type: companyTypeValue,
      terminalTierThresholdHours: terminalTierThresholdHours,
      terminalTierThresholdDirection: terminalTierThresholdDirection,
      pastShiftCreationAllowed: pastShiftCreationAllowedValue,
    },
  }

  const methods = useForm<EditGeneralDetailsFlowSchema>({
    resolver: zodResolver(editGeneralDetailsFlowSchema),
    values: {
      ...realFormValues,
      type: formData.type ?? companyTypeValue,
      terminalTierThresholdHours:
        formData.terminalTierThresholdHours ?? terminalTierThresholdHours,
      terminalTierThresholdDirection:
        formData.terminalTierThresholdDirection ??
        terminalTierThresholdDirection ??
        'before_shift_start',
      pastShiftCreationAllowed:
        formData.pastShiftCreationAllowed ?? pastShiftCreationAllowedValue,
    },
  })

  const { handleSubmit, reset } = methods

  // Discard in-progress edits so reopening the dialog shows reality, not the
  // abandoned draft that react-hook-form would otherwise retain.
  const handleClose = () => {
    setFormData({})
    reset(realFormValues)
    close()
  }

  const onSubmit = handleSubmit((data) => {
    setFormData({
      type: data.type,
      terminalTierThresholdHours: data.terminalTierThresholdHours,
      terminalTierThresholdDirection: data.terminalTierThresholdDirection,
      pastShiftCreationAllowed: data.pastShiftCreationAllowed,
    })

    const changedFields = Object.keys(data.currentValues).filter(
      (key): key is keyof typeof data.currentValues =>
        JSON.stringify(data[key as keyof typeof data]) !==
        JSON.stringify(data.currentValues[key])
    )

    // It is imporant that we only pass the changed fields to the server.
    const changedFieldsObject = changedFields.reduce(
      (acc, key) => {
        return {
          ...acc,
          [key]: data[key as keyof typeof data],
        }
      },
      {} as Partial<typeof data>
    )

    const nextFields = companyTypeToFields(data.type)
    const originalFields = companyTypeToFields(data.currentValues.type)

    submitFormToServer(
      {
        type:
          nextFields.type !== originalFields.type ? nextFields.type : undefined,
        requiresEverify:
          nextFields.requiresEverify !== originalFields.requiresEverify
            ? nextFields.requiresEverify
            : undefined,
        terminalTierThresholdHours:
          changedFieldsObject.terminalTierThresholdHours,
        terminalTierThresholdDirection:
          changedFieldsObject.terminalTierThresholdDirection,
        pastShiftCreationAllowed: changedFieldsObject.pastShiftCreationAllowed,
      },
      {
        onSuccess: () => {
          toast.success('Updated general details successfully!', {
            position: 'top-center',
          })
          setFormData({})
          reset()
          close()
        },
      }
    )
  })

  return (
    <ModalWithSelect
      open={isOpen}
      onClose={handleClose}
      position="top"
      loading={isPending}
      showCancel
      customCta={
        <Flex>
          <Button variant="text" onClick={handleClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={onSubmit}>
            Save
          </Button>
        </Flex>
      }
    >
      <Heading level={3} fontWeight="400" style={{ margin: 0 }}>
        Edit General details
      </Heading>

      <FormProvider {...methods}>
        <GeneralDetailsForm onSubmit={onSubmit} />
      </FormProvider>
    </ModalWithSelect>
  )
}
